/**
 * PerlinContour — animacja poziomic
 * @author Mikhaell
 * @basedOn https://codepen.io/angularek/pen/ByNQvQe
 */

// import noise from 'noisejs'; // <- lokalna wersja lub npm, patrz niżej
import { Noise } from 'noisejs';
const noise = new Noise(Math.random());

export class PerlinContourRenderer {
  constructor(canvasElement) {
    const BASE_CONFIG = {
      showFPS: false,
      MAX_FPS: 40,
      thresholdIncrement: 10,
      thickLineThresholdMultiple: 103,
      res: 12,
      baseZOffset: 0.0011,
      lineColor: '#f0f0f0',
    };

    this.canvas = canvasElement;
    this.ctx = canvasElement.getContext('2d');
    if (!this.ctx) return;

    this.config = { ...BASE_CONFIG };
    if (canvasElement.dataset.res) this.config.res = +canvasElement.dataset.res;
    if (canvasElement.dataset.lineColor) this.config.lineColor = canvasElement.dataset.lineColor;
    if (canvasElement.dataset.thresholdIncrement)
      this.config.thresholdIncrement = +canvasElement.dataset.thresholdIncrement;
    if (canvasElement.dataset.baseZOffset)
      this.config.baseZOffset = +canvasElement.dataset.baseZOffset;

    this.mousePos = { x: -99, y: -99 };
    this.zOffset = 0;
    this.noiseMin = 100;
    this.noiseMax = 0;
    this.cols = 0;
    this.rows = 0;
    this.inputValues = [];
    this.zBoostValues = [];
    this.currentThreshold = 0;
    this.frameValues = [];

    this.canvas.addEventListener('mousemove', (e) => {
      this.mousePos = { x: e.offsetX, y: e.offsetY };
    });

    this.canvasSize();
    window.addEventListener('resize', () => this.canvasSize());
    this.animateLoop();
  }

  canvasSize() {
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = rect.width * window.devicePixelRatio;
    this.canvas.height = rect.height * window.devicePixelRatio;
    this.ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    this.canvas.style.width = rect.width + 'px';
    this.canvas.style.height = rect.height + 'px';

    this.cols = Math.floor(this.canvas.width / this.config.res) + 1;
    this.rows = Math.floor(this.canvas.height / this.config.res) + 1;

    this.zBoostValues = Array.from({ length: this.rows }, () =>
      Array(this.cols).fill(0)
    );
  }

  animateLoop() {
    setTimeout(() => requestAnimationFrame(() => this.animateLoop()), 1000 / this.config.MAX_FPS);

    if (!this.isInViewport()) return;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.zOffset += this.config.baseZOffset;
    this.generateNoise();

    const roundedMin = Math.floor(this.noiseMin / this.config.thresholdIncrement) * this.config.thresholdIncrement;
    const roundedMax = Math.ceil(this.noiseMax / this.config.thresholdIncrement) * this.config.thresholdIncrement;

    for (let t = roundedMin; t < roundedMax; t += this.config.thresholdIncrement) {
      this.currentThreshold = t;
      this.renderAtThreshold();
    }

    this.noiseMin = 100;
    this.noiseMax = 0;
  }

  generateNoise() {
    const s = 0.02;
    for (let y = 0; y < this.rows; y++) {
      this.inputValues[y] = [];
      for (let x = 0; x < this.cols; x++) {
        const value = noise.perlin3(x * s, y * s, this.zOffset + this.zBoostValues[y][x]) * 100;
        this.inputValues[y][x] = value;
        if (value < this.noiseMin) this.noiseMin = value;
        if (value > this.noiseMax) this.noiseMax = value;
        if (this.zBoostValues[y][x] > 0) this.zBoostValues[y][x] *= 0.99;
      }
    }
  }

  renderAtThreshold() {
    this.ctx.beginPath();
    this.ctx.strokeStyle = this.config.lineColor;
    this.ctx.lineWidth =
      this.currentThreshold %
        (this.config.thresholdIncrement * this.config.thickLineThresholdMultiple) ===
      0
        ? 2
        : 1;

    const res = this.config.res;
    for (let y = 0; y < this.rows - 1; y++) {
      for (let x = 0; x < this.cols - 1; x++) {
        const nw = this.inputValues[y][x];
        const ne = this.inputValues[y][x + 1];
        const se = this.inputValues[y + 1][x + 1];
        const sw = this.inputValues[y + 1][x];

        if ([nw, ne, se, sw].every((v) => v > this.currentThreshold)) continue;
        if ([nw, ne, se, sw].every((v) => v < this.currentThreshold)) continue;

        const gridType = this.binaryToType(
          nw > this.currentThreshold,
          ne > this.currentThreshold,
          se > this.currentThreshold,
          sw > this.currentThreshold
        );

        this.placeLines(gridType, x, y);
      }
    }

    this.ctx.stroke();
  }

  binaryToType(nw, ne, se, sw) {
    return [nw, ne, se, sw].reduce((acc, v) => (acc << 1) | (v ? 1 : 0), 0);
  }

  placeLines(type, x, y) {
    const r = this.config.res;
    const nw = this.inputValues[y][x];
    const ne = this.inputValues[y][x + 1];
    const se = this.inputValues[y + 1][x + 1];
    const sw = this.inputValues[y + 1][x];
    const lerp = this.linInterpolate.bind(this);
    let a, b, c, d;

    switch (type) {
      case 1: case 14:
        c = [x * r + r * lerp(sw, se), y * r + r];
        d = [x * r, y * r + r * lerp(nw, sw)];
        this.line(d, c);
        break;
      case 2: case 13:
        b = [x * r + r, y * r + r * lerp(ne, se)];
        c = [x * r + r * lerp(sw, se), y * r + r];
        this.line(b, c);
        break;
      case 3: case 12:
        b = [x * r + r, y * r + r * lerp(ne, se)];
        d = [x * r, y * r + r * lerp(nw, sw)];
        this.line(d, b);
        break;
      case 4: case 11:
        a = [x * r + r * lerp(nw, ne), y * r];
        b = [x * r + r, y * r + r * lerp(ne, se)];
        this.line(a, b);
        break;
      case 5:
        a = [x * r + r * lerp(nw, ne), y * r];
        b = [x * r + r, y * r + r * lerp(ne, se)];
        c = [x * r + r * lerp(sw, se), y * r + r];
        d = [x * r, y * r + r * lerp(nw, sw)];
        this.line(d, a);
        this.line(c, b);
        break;
      case 6: case 9:
        a = [x * r + r * lerp(nw, ne), y * r];
        c = [x * r + r * lerp(sw, se), y * r + r];
        this.line(c, a);
        break;
      case 7: case 8:
        a = [x * r + r * lerp(nw, ne), y * r];
        d = [x * r, y * r + r * lerp(nw, sw)];
        this.line(d, a);
        break;
      case 10:
        a = [x * r + r * lerp(nw, ne), y * r];
        b = [x * r + r, y * r + r * lerp(ne, se)];
        c = [x * r + r * lerp(sw, se), y * r + r];
        d = [x * r, y * r + r * lerp(nw, sw)];
        this.line(a, b);
        this.line(c, d);
        break;
    }
  }

  linInterpolate(x0, x1, y0 = 0, y1 = 1) {
    if (x0 === x1) return 0;
    return y0 + ((y1 - y0) * (this.currentThreshold - x0)) / (x1 - x0);
  }

  line(from, to) {
    this.ctx.moveTo(from[0], from[1]);
    this.ctx.lineTo(to[0], to[1]);
  }

  isInViewport() {
    const rect = this.canvas.getBoundingClientRect();
    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth
    );
  }
}

export function initPoziomice() {
  noise.seed(Math.random());
  document.querySelectorAll('.res-canvas').forEach((canvas) => {
    new PerlinContourRenderer(canvas);
  });
}